import type { Element, Root as HastRoot } from "hast";
import type { Parent, PhrasingContent, Root as MdastRoot, RootContent } from "mdast";
import type { IChatMention } from "@/types/(my-space)/chat.interface";
import { splitMessageText } from "./mentions";

/**
 * Chat text is Markdown (GFM): **bold**, *italic*, ~~strike~~, `code`,
 * ```blocks```, > quotes, lists, [links](url), tables. Stored as typed — the
 * mobile app reads the same syntax.
 */

// ─── Detection ─────────────────────────────────────────────────────────────

/**
 * Anything Markdown would draw differently from plain text. Messages without
 * it keep the plain renderer (exact whitespace, nothing reinterpreted).
 */
const MARKDOWN_HINT =
  /\*\*|__|~~|`|\[[^\]\n]+\]\([^)\s]+\)|(^|[\s(])[*_][^\s*_]|^ {0,3}(#{1,6}\s|>|[-*+]\s+\S|\d{1,9}[.)]\s+\S|\|.*\|)/m;

export const hasMarkdown = (text: string | null | undefined) => !!text && MARKDOWN_HINT.test(text);

// ─── Previews ──────────────────────────────────────────────────────────────

/**
 * One line of a formatted message for previews (chat list, popups, replies,
 * notifications): the words without the symbols. Not a parser — good enough
 * for a truncated line, never shown where the formatting matters.
 */
export const stripMarkdown = (text: string): string => {
  if (!hasMarkdown(text)) return text;
  return (
    text
      // Fenced code: keep the code, drop the fences (and language tag).
      .replace(/^ {0,3}(```|~~~)[^\n]*\n?/gm, "")
      // [label](url) → label; ![alt](url) → alt.
      .replace(/!?\[([^\]\n]*)\]\([^)\s]+\)/g, "$1")
      // Line starts: headings, quotes, list bullets, table pipes.
      .replace(/^ {0,3}#{1,6}\s+/gm, "")
      .replace(/^ {0,3}>\s?/gm, "")
      .replace(/^(\s*)[-*+]\s+(\[[ xX]\]\s+)?/gm, "$1")
      .replace(/^ {0,3}\|?(.*?)\|?\s*$/gm, (line, inner: string) =>
        line.includes("|") ? inner.trim().replace(/\s*\|\s*/g, " · ").replace(/^[\s·:-]+$/, "") : line,
      )
      // Emphasis and code markers around words.
      .replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, "$2")
      .replace(/~~(?=\S)([\s\S]*?\S)~~/g, "$1")
      .replace(/(^|[^\w*])\*(?=\S)([^*\n]*?\S)\*(?!\w)/g, "$1$2")
      .replace(/(^|[^\w_])_(?=\S)([^_\n]*?\S)_(?!\w)/g, "$1$2")
      .replace(/`+([^`]+)`+/g, "$1")
      .replace(/\n{2,}/g, "\n")
      .trim()
  );
};

// ─── Rendering (remark / rehype plugins) ───────────────────────────────────

/** Where @mentions and leftover bare links become nodes the bubble draws. */
type MentionNode = {
  type: "chatMention";
  data: { hName: "span"; hProperties: { dataMention: string } };
  children: [{ type: "text"; value: string }];
};

/** Nodes whose text is left exactly as typed. */
const VERBATIM = new Set(["code", "inlineCode", "link", "linkReference"]);

/** Containers of blocks: raw HTML directly inside one becomes its own paragraph. */
const BLOCK_PARENTS = new Set(["root", "blockquote", "listItem"]);

const phrasingFor = (value: string, mentions: IChatMention[]): PhrasingContent[] => {
  const out: PhrasingContent[] = [];
  value.split("\n").forEach((line, i) => {
    // Chat Enter is a line break, not a soft wrap.
    if (i > 0) out.push({ type: "break" });
    if (!line) return;
    splitMessageText(line, mentions).forEach((part) => {
      if (part.kind === "mention") {
        const node: MentionNode = {
          type: "chatMention",
          data: { hName: "span", hProperties: { dataMention: String(part.userId) } },
          children: [{ type: "text", value: part.text }],
        };
        out.push(node as unknown as PhrasingContent);
      } else if (part.kind === "link") {
        out.push({ type: "link", url: part.text, children: [{ type: "text", value: part.text }] });
      } else {
        out.push({ type: "text", value: part.text });
      }
    });
  });
  return out;
};

const walk = (node: Parent, mentions: IChatMention[]) => {
  const next: RootContent[] = [];
  for (const child of node.children as RootContent[]) {
    if (child.type === "text") {
      next.push(...(phrasingFor(child.value, mentions) as RootContent[]));
      continue;
    }
    // Raw HTML is shown as the characters typed — never rendered.
    if (child.type === "html") {
      const phrasing = phrasingFor(child.value, mentions);
      if (BLOCK_PARENTS.has(node.type)) next.push({ type: "paragraph", children: phrasing });
      else next.push(...(phrasing as RootContent[]));
      continue;
    }
    if (!VERBATIM.has(child.type) && "children" in child) walk(child as Parent, mentions);
    next.push(child);
  }
  node.children = next as Parent["children"];
};

/**
 * Line breaks as typed, @mentions / bare links outside code and links, and
 * raw HTML as plain text.
 */
export const remarkChatText = (mentions: IChatMention[] | null | undefined) => () => (tree: MdastRoot) => {
  walk(tree, (mentions ?? []).filter((m) => m.name));
};

/**
 * Leaves a `<span data-footer-slot>` where the bubble's time can sit inline:
 * at the end of the last paragraph or heading, else after everything.
 */
export const rehypeFooterSlot = () => (tree: HastRoot) => {
  const slot: Element = {
    type: "element",
    tagName: "span",
    properties: { dataFooterSlot: "" },
    children: [],
  };
  const blocks = tree.children.filter((c): c is Element => c.type === "element");
  const last = blocks[blocks.length - 1];
  if (last && /^(p|h[1-6])$/.test(last.tagName)) last.children.push(slot);
  else tree.children.push(slot);
};

// ─── Composer helpers ──────────────────────────────────────────────────────

export interface TextEdit {
  text: string;
  selectionStart: number;
  selectionEnd: number;
}

/**
 * Wraps the selection in `marker` (or the word placeholder when nothing is
 * selected); unwraps it if it's already wrapped, so the button toggles.
 */
export const toggleWrap = (
  text: string,
  start: number,
  end: number,
  marker: string,
  placeholder: string,
): TextEdit => {
  const before = text.slice(0, start);
  const selected = text.slice(start, end);
  const after = text.slice(end);
  const m = marker.length;
  if (before.endsWith(marker) && after.startsWith(marker)) {
    return {
      text: before.slice(0, -m) + selected + after.slice(m),
      selectionStart: start - m,
      selectionEnd: end - m,
    };
  }
  if (selected.length > 2 * m && selected.startsWith(marker) && selected.endsWith(marker)) {
    const inner = selected.slice(m, -m);
    return { text: before + inner + after, selectionStart: start, selectionEnd: start + inner.length };
  }
  const inner = selected || placeholder;
  return {
    text: before + marker + inner + marker + after,
    selectionStart: start + m,
    selectionEnd: start + m + inner.length,
  };
};

/** A fenced block around the selected lines (or an empty one to type into). */
export const wrapCodeBlock = (text: string, start: number, end: number): TextEdit => {
  const before = text.slice(0, start);
  const selected = text.slice(start, end);
  const after = text.slice(end);
  const lead = before && !before.endsWith("\n") ? "\n" : "";
  const trail = after && !after.startsWith("\n") ? "\n" : "";
  const open = `${lead}\`\`\`\n`;
  return {
    text: `${before}${open}${selected}\n\`\`\`${trail}${after}`,
    selectionStart: start + open.length,
    selectionEnd: start + open.length + selected.length,
  };
};

/**
 * Toggles a line prefix ("> ", "- ", "1. ") on every line the selection
 * touches; numbered lists count up.
 */
export const toggleLinePrefix = (
  text: string,
  start: number,
  end: number,
  kind: "quote" | "bullet" | "numbered",
): TextEdit => {
  const lineStart = text.lastIndexOf("\n", start - 1) + 1;
  const nl = text.indexOf("\n", end);
  const lineEnd = nl === -1 ? text.length : nl;
  const lines = text.slice(lineStart, lineEnd).split("\n");
  const pattern = kind === "quote" ? /^> ?/ : kind === "bullet" ? /^[-*+] / : /^\d+[.)] /;
  const allHave = lines.every((l) => pattern.test(l));
  const next = lines.map((l, i) => {
    if (allHave) return l.replace(pattern, "");
    const bare = l.replace(/^(> ?|[-*+] |\d+[.)] )/, "");
    return (kind === "quote" ? "> " : kind === "bullet" ? "- " : `${i + 1}. `) + bare;
  });
  const block = next.join("\n");
  return {
    text: text.slice(0, lineStart) + block + text.slice(lineEnd),
    selectionStart: lineStart,
    selectionEnd: lineStart + block.length,
  };
};

/** [selection](url) — the url left selected to paste over. */
export const insertLink = (text: string, start: number, end: number, placeholder: string): TextEdit => {
  const label = text.slice(start, end) || placeholder;
  const url = "https://";
  const inserted = `[${label}](${url})`;
  const urlAt = start + label.length + 3;
  return {
    text: text.slice(0, start) + inserted + text.slice(end),
    selectionStart: urlAt,
    selectionEnd: urlAt + url.length,
  };
};

export type FormatKind =
  | "bold"
  | "italic"
  | "strike"
  | "code"
  | "codeBlock"
  | "quote"
  | "bullet"
  | "numbered"
  | "link";

/** One toolbar button / shortcut applied to the composer's text. */
export const applyFormat = (
  kind: FormatKind,
  text: string,
  start: number,
  end: number,
  placeholder: (kind: FormatKind) => string,
): TextEdit => {
  switch (kind) {
    case "bold":
      return toggleWrap(text, start, end, "**", placeholder(kind));
    case "italic":
      return toggleWrap(text, start, end, "_", placeholder(kind));
    case "strike":
      return toggleWrap(text, start, end, "~~", placeholder(kind));
    case "code":
      return toggleWrap(text, start, end, "`", placeholder(kind));
    case "codeBlock":
      return wrapCodeBlock(text, start, end);
    case "quote":
    case "bullet":
    case "numbered":
      return toggleLinePrefix(text, start, end, kind);
    case "link":
      return insertLink(text, start, end, placeholder(kind));
  }
};

/** Ctrl/⌘ shortcuts in the composer. */
export const formatForShortcut = (e: {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}): FormatKind | null => {
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return null;
  const key = e.key.toLowerCase();
  if (e.shiftKey) return key === "x" ? "strike" : null;
  return key === "b" ? "bold" : key === "i" ? "italic" : key === "e" ? "code" : null;
};

/**
 * The caret is inside a ``` block that isn't closed yet: Enter should add a
 * line of code there, not send.
 */
export const insideOpenFence = (text: string, caret: number) =>
  (text.slice(0, caret).match(/^ {0,3}```/gm)?.length ?? 0) % 2 === 1;
